import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash, RotateCcw } from "lucide-react";
import { toast } from "react-toastify";
import SearchBar from "../../components/SearchBar";
import LoadingOverlay from "../../components/LoadingOverlay";
import Pagination from "../../components/Pagination";


function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const ITEMS_PER_PAGE = 30;
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSuppliers();
    fetchCategories();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const res = await api.get("/supplier-categories");
      setCategories(res.data);
    } catch (error) {
      console.error("Error al cargar categorías");
    }
  };

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const res = await api.get("/suppliers");
      setSuppliers(res.data || []);
    } catch (error) {
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = suppliers.filter((supplier) => {
    const matchesSearch = `${supplier.name} ${supplier.nit}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    // Si selectedCategory es vacío, pasan todos. Si tiene valor, comparamos IDs.
    const matchesCategory = selectedCategory === ""
      ? true
      : supplier.supplier_category_id === parseInt(selectedCategory);

    return matchesSearch && matchesCategory;
  });

  const pageCount = Math.ceil(filteredSuppliers.length / ITEMS_PER_PAGE);

  const currentItems = filteredSuppliers.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const handleToggleStatus = async (id, currentStatus) => {
    const isActivating = currentStatus === 0;
    const message = isActivating
      ? "¿Deseas restablecer este proveedor?"
      : "¿Estás seguro de que deseas desactivar este proveedor?";

    if (!window.confirm(message)) return;

    try {
      setLoading(true);

      if (isActivating) {

        // Opción B: Usas el update normal enviando status 1
        await api.put(`/suppliers/${id}`, { status: 1 });
      } else {
        // Usas tu ruta de delete que ya cambia el status a 0
        await api.delete(`/suppliers/${id}`);
      }

      toast.success(isActivating ? "Proveedor restablecido" : "Proveedor desactivado");
      await fetchSuppliers(); // Recargamos la lista
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      toast.error("No se pudo cambiar el estado del proveedor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md relative">
      <LoadingOverlay loading={loading} />

      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        {/* Título a la izquierda */}
        <h2 className="text-3xl font-semibold text-gray-800">Proveedores</h2>

        {/* Contenedor de herramientas agrupado a la derecha */}
        <div className="flex items-center gap-3">

          <div className="w-64">
            <SearchBar
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="border p-2 rounded"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Todas las categorías</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>

          <Link
            to="/suppliers/create"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow flex items-center gap-2 transition duration-300 whitespace-nowrap"
          >
            <Plus size={18} />
            <span>Nuevo Proveedor</span>
          </Link>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl shadow">
        <table className="min-w-full bg-white table-auto">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center text-sm uppercase tracking-wider">
              <th className="px-6 py-3">Nombre</th>
              <th className="px-6 py-3">NIT</th>
              <th className="px-6 py-3">Dirección</th>
              <th className="px-6 py-3">Teléfono</th>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3 text-center w-28">Acciones</th>
            </tr>
          </thead>
          <tbody className="text-center">
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-6 text-center text-gray-400 italic">
                  No hay registros.
                </td>
              </tr>
            ) : (
              currentItems.map((supplier, index) => (
                <tr
                  key={supplier.id}
                  className={`border-t transition-colors ${supplier.status === 0 ? "bg-gray-200 opacity-60" : index % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                >
                  <td className="px-6 py-4 font-medium">{supplier.name}</td>
                  <td className="px-6 py-4">{supplier.nit}</td>
                  <td className="px-6 py-4">{supplier.address}</td>
                  <td className="px-6 py-4">{supplier.phone}</td>
                  <td className="px-6 py-4">{supplier.email}</td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center gap-2">
                      {/* Botón Editar */}
                      <Link
                        to={`/suppliers/edit/${supplier.id}`}
                        className="bg-yellow-400 hover:bg-yellow-500 text-white p-2 rounded-lg shadow transition-all duration-300"
                        title="Editar"
                      >
                        <Pencil size={16} />
                      </Link>

                      {/* Botón Dinámico: Eliminar / Restablecer */}
                      <button
                        onClick={() => handleToggleStatus(supplier.id, supplier.status)}
                        className={`p-2 rounded-lg shadow transition-all duration-300 text-white ${supplier.status === 0
                          ? "bg-blue-500 hover:bg-blue-600" // Azul para restablecer
                          : "bg-red-500 hover:bg-red-600"   // Rojo para desactivar
                          }`}
                        title={supplier.status === 0 ? "Restablecer Proveedor" : "Desactivar Proveedor"}
                      >
                        {supplier.status === 0 ? (
                          <RotateCcw size={16} /> // Icono de flecha devolviéndose
                        ) : (
                          <Trash size={16} />     // Icono de basura
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <Pagination
          pageCount={pageCount}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          currentItemsCount={currentItems.length}
          totalItemsCount={filteredSuppliers.length}
        />

      </div>
    </div>
  );
}

export default Suppliers;
